import { AlertTriangle, CheckCircle2, Clock3, Cloud, ShieldAlert, XCircle } from 'lucide-react';

import { formatDate } from '../lib';
import type { ConnectivityProbe, ProbeTcpStatus, ProbeTlsStatus } from '../types';

const tcpLabels: Record<ProbeTcpStatus, string> = {
  reachable: '可达',
  timeout: '超时',
  unreachable: '不可达',
  platform_blocked: '平台阻止',
  unsupported: '不支持',
};

const tlsLabels: Record<ProbeTlsStatus, string> = {
  reachable: '握手成功',
  timeout: '超时',
  failed: '握手失败',
  unverifiable: '无法验证',
  not_applicable: '不适用',
};

function resultTone(status: ProbeTcpStatus | ProbeTlsStatus) {
  if (status === 'reachable') return 'success';
  if (status === 'timeout' || status === 'unverifiable' || status === 'unsupported' || status === 'not_applicable') return 'warning';
  return 'error';
}

function StatusIcon({ tone }: { tone: ReturnType<typeof resultTone> }) {
  if (tone === 'success') return <CheckCircle2 size={14} aria-hidden="true" />;
  if (tone === 'warning') return <AlertTriangle size={14} aria-hidden="true" />;
  return <XCircle size={14} aria-hidden="true" />;
}

function ProbeStatus({ label, status, latencyMs }: { label: string; status: ProbeTcpStatus | ProbeTlsStatus; latencyMs: number | null }) {
  const tone = resultTone(status);
  const statusText = label === 'TCP' ? tcpLabels[status as ProbeTcpStatus] : tlsLabels[status as ProbeTlsStatus];
  return <div className="probe-stage">
    <span className="probe-stage-label">{label}</span>
    <span className={`probe-status probe-status-${tone}`}><StatusIcon tone={tone} />{statusText}</span>
    <span className="probe-latency">{latencyMs == null ? '—' : `${Math.round(latencyMs)} ms`}</span>
  </div>;
}

function formatEndpoint(server: string | null, port: number | null): string {
  if (!server || !port) return '无有效端点';
  return `${server.includes(':') ? `[${server}]` : server}:${port}`;
}

export function ConnectivityResults({ probe }: { probe: ConnectivityProbe }) {
  return <section className="probe-results" aria-label="云端连通性检测结果">
    <div className="probe-summary">
      <div><Cloud size={16} aria-hidden="true" /><span>Cloudflare PoP</span><strong>{probe.colo || '未知'}</strong></div>
      <div><Clock3 size={16} aria-hidden="true" /><span>测试时间</span><strong>{formatDate(probe.testedAt)}</strong></div>
      <div><ShieldAlert size={16} aria-hidden="true" /><span>检测节点</span><strong>{probe.testedCount} / {probe.totalCount}</strong></div>
    </div>

    {probe.truncated && <div className="probe-notice" role="note">来源包含 {probe.totalCount} 个处理后节点，本次仅检测前 {probe.testedCount} 个。</div>}
    {probe.results.length === 0 ? <div className="inline-empty compact">来源中没有可检测的节点。</div> : <ol className="probe-list">
      {probe.results.map((result) => <li key={result.index} className="probe-node">
        <div className="probe-node-main">
          <span className="probe-node-index">{result.index + 1}</span>
          <div><strong>{result.name || '（空名称）'}</strong><span>{result.type || 'unknown'} · {formatEndpoint(result.server, result.port)}</span></div>
        </div>
        <div className="probe-stages">
          <ProbeStatus label="TCP" status={result.tcp.status} latencyMs={result.tcp.latencyMs} />
          <ProbeStatus label="TLS" status={result.tls.status} latencyMs={result.tls.latencyMs} />
        </div>
      </li>)}
    </ol>}

    <p className="probe-caveat">这不是测速。结果仅表示当前 Cloudflare 边缘到节点端点的 TCP/TLS 可达性，不代表用户本地延迟、代理凭据或真实转发可用。</p>
  </section>;
}
